import { Box } from '@mui/system'
import { LoadingComponent } from 'Components/HelperComponents'
import { MeetingTable } from 'NewVersion/components/Meeting/MeetingTable'
import { ScheduleTable } from 'NewVersion/components/Schedule/ScheduleTable'
import { Filter } from 'NewVersion/components/UI/Filters/Filter'
import FilterSearch from 'NewVersion/components/UI/Filters/FilterSearch'
import { MAX_TIEMS_PAGE_DEFAULT, Pagination } from 'NewVersion/components/UI/Pagination/Pagination'
import { CalendarIcon } from 'NewVersion/icons/CalendarIcon'
import { MessageIcon } from 'NewVersion/icons/MessageIcon'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import 'NewVersion/styles/AgendaListPage.scss'
import { useEdition } from 'hooks/useEdition'
import { useFavourites } from 'hooks/useFavourites'
import { useGuillotina } from 'hooks/useGuillotina'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { getCurrentJWToken, getCurrentLanguage, getEmailFromToken } from 'utils/utils'

const CHUNK_SIZE = 30

// Helper function to split an array into chunks
const chunkArray = (array, size) => {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

function AgendaListPage() {
  const { t } = useTranslation()
  const { edition, currentEdition } = useEdition()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentLanguage = getCurrentLanguage()
  const { favouiteIds, loading: loadingFavourites } = useFavourites()
  const token = getCurrentJWToken()

  const [favouriteShows, setFavouriteShows] = useState([])
  const [showingItems, setShowingItems] = useState(0)
  const [itemsTotal, setItemsTotal] = useState(0)
  const [isLoadingFavourites, setIsLoadingFavourites] = useState(false)

  // Memoize filter values that affect server requests (excluding 'page')
  const serverFilters = useMemo(
    () => ({
      typeEvent: searchParams.get('type_event'),
      day: searchParams.get('day'),
      search: searchParams.get('search'),
      sortDirection: searchParams.get('sort-direction'),
      sortBy: searchParams.get('sort-by'),
    }),
    [
      searchParams.get('type_event'),
      searchParams.get('day'),
      searchParams.get('search'),
      searchParams.get('sort-direction'),
      searchParams.get('sort-by'),
    ]
  )

  // Function to create favorite filter for a specific chunk
  const getFiltreFavouriteForChunk = useCallback((favouriteChunk) => {
    return favouriteChunk && favouriteChunk.length > 0
      ? `&uuid__or=${favouriteChunk.map((item) => `uuid=${item}`).join('%26')}`
      : ''
  }, [])

  const getFiltersSearch = useCallback(
    (favouriteChunk = null) => {
      let search = ''
      let filterTypeName = ''
      let filterDay = ''

      if (serverFilters.typeEvent === 'act_prof') {
        filterTypeName = 'type_name=ActivitatProfessional'
      } else if (serverFilters.typeEvent === 'act_art') {
        filterTypeName = 'type_name=Actuacio'
      } else {
        filterTypeName = 'type_name__in=ActivitatProfessional,Actuacio'
      }

      if (serverFilters.day && serverFilters.day !== 'all') {
        const dayArray = serverFilters.day.split(',')
        filterDay = `&data_timestamp__or=${dayArray
          .map((item) => `data_timestamp=${item}`)
          .join('%26')}`
      }

      if (serverFilters.search && serverFilters.search !== '') {
        search = `&__or=${['artista', `nom_${currentLanguage}`, 'shows_names']
          .map((item) => `${item}=${serverFilters.search}`)
          .join('%26')}`
      }

      const filterOrder = `&_sort_${
        serverFilters.sortDirection ? serverFilters.sortDirection : 'des'
      }=${serverFilters.sortBy ? serverFilters.sortBy : 'data'}`

      const favouriteFilter = favouriteChunk ? getFiltreFavouriteForChunk(favouriteChunk) : ''

      return `${filterTypeName}${filterDay}${favouriteFilter}${search}${filterOrder}`
    },
    [serverFilters, currentLanguage, getFiltreFavouriteForChunk]
  )

  const getFiltersSearchMeet = () => {
    const filterOrder = `&_sort_${
      searchParams.get('sort-direction') ? searchParams.get('sort-direction') : 'des'
    }=${searchParams.get('sort-by') ? searchParams.get('sort-by') : 'data'}`

    return `${filterOrder}`
  }

  // useEffect to make favorite requests in chunks
  useEffect(() => {
    const fetchFavouriteShows = async () => {
      if (loadingFavourites || !edition) {
        return
      }

      if (!favouiteIds || favouiteIds.length === 0) {
        setFavouriteShows([])
        setShowingItems(0)
        setItemsTotal(0)
        setIsLoadingFavourites(false)
        return
      }

      setIsLoadingFavourites(true)

      try {
        const chunks = chunkArray(favouiteIds, CHUNK_SIZE)
        const promisesWithFilters = chunks.map((chunk) => {
          const query = `${edition}/@search?${getFiltersSearch(
            chunk
          )}&b_start=0&b_size=${CHUNK_SIZE}`
          return fetchGuillotina({
            path: query,
            token,
            checkError: true,
          })
        })

        const promisesTotal = chunks.map((chunk) => {
          const favouriteFilter = getFiltreFavouriteForChunk(chunk)
          const query = `${edition}/@search?type_name__in=Actuacio,ActivitatProfessional${favouriteFilter}&b_start=0&b_size=1`
          return fetchGuillotina({
            path: query,
            token,
            checkError: true,
          })
        })

        // Execute all requests in parallel
        const [resultsWithFilters, resultsTotal] = await Promise.all([
          Promise.all(promisesWithFilters),
          Promise.all(promisesTotal),
        ])

        // Combine results with filters
        const allItems = []
        let totalItemsWithFilters = 0

        resultsWithFilters.forEach((result) => {
          if (result && result.items) {
            allItems.push(...result.items)
          }
          if (result && result.items_total) {
            totalItemsWithFilters += result.items_total
          }
        })

        // Calculate total without filters
        let totalItemsWithoutFilters = 0
        resultsTotal.forEach((result) => {
          if (result && result.items_total) {
            totalItemsWithoutFilters += result.items_total
          }
        })

        setFavouriteShows(allItems)
        setShowingItems(totalItemsWithFilters)
        setItemsTotal(totalItemsWithoutFilters)
      } catch (error) {
        console.error('Error fetching favourite shows:', error)
        setFavouriteShows([])
        setShowingItems(0)
        setItemsTotal(0)
      } finally {
        setIsLoadingFavourites(false)
      }
    }

    fetchFavouriteShows()
  }, [edition, favouiteIds, loadingFavourites, token, getFiltersSearch, getFiltreFavouriteForChunk])

  const { data: { items: acreditation } = [], isLoading: isLoadingAcreditation } = useGuillotina({
    path: `${edition}/@search?type_name=AcreditacioProfessional&owners=${getEmailFromToken()}&correu=${getEmailFromToken()}&b_start=0&b_size=1`,
  })

  const {
    refresh,
    data: { items: meetings } = [],
    isLoading: isLoadingMeetings,
  } = useGuillotina({
    path:
      acreditation && acreditation[0] && acreditation[0].id
        ? `${edition}/@search?type_name=FranjaReunioRapida${getFiltersSearchMeet()}&acreditat=${
            acreditation[0].id
          }`
        : null,
  })

  // Client-side pagination
  const paginatedFavouriteShows = useMemo(() => {
    const currentPage = parseInt(searchParams.get('page') || '1')
    const startIndex = (currentPage - 1) * MAX_TIEMS_PAGE_DEFAULT
    const endIndex = startIndex + MAX_TIEMS_PAGE_DEFAULT
    return favouriteShows.slice(startIndex, endIndex)
  }, [favouriteShows, searchParams])

  const filtersDefiniton = useMemo(() => {
    const filters = [
      {
        key: 'type_event',
        label: t('Activitat'),
        sorted: true,
        unique: true,
        data: [
          { value: 'all', label: t('show_all'), default: true },
          { value: 'quick_meet', label: t('reunions_rapides') },
          { value: 'act_art', label: t('Activitat artística') },
          { value: 'act_prof', label: t('Activitat professional') },
        ],
      },
    ]
    if (currentEdition) {
      filters.push({
        key: 'day',
        label: t('day'),
        sorted: false,
        data: [
          { value: 'all', label: t('show_all_days'), default: true },
          ...currentEdition
            .getFiraProfessionalActivitiesDaysFilters(t)
            .map((day) => ({ value: day[2], label: `${day[0]} ${day[1]}` })),
        ],
      })
    }

    return filters
  }, [currentEdition, currentLanguage])

  return (
    <Box className="container-agenda page-wrapper">
      <Box
        className={'laMevaAgendaTitle'}
        display={'flex'}
        alignItems={'end'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
      >
        <Box maxWidth={'500px'}>
          <h1>{t('agenda')}</h1>
          <p>{t('agenda_page_desc')}</p>
        </Box>
        <FilterSearch
          placeholder={t('agenda_search')}
          onChange={({ target }) => {
            searchParams.set('search', target.value)
            searchParams.set('page', 1)
            setSearchParams(searchParams)
          }}
          dataTest="inputSearchAgendaTest"
          value={searchParams.get('search') || ''}
          variant={'outlined'}
        />
      </Box>
      <Filter
        itemsTotal={itemsTotal}
        showingItems={showingItems || 0}
        filtersDefiniton={filtersDefiniton}
        showTypeView={false}
      />
      <Box className="agenda-title">
        <h2>{t('meves_reunions')}</h2>
        <MessageIcon />
      </Box>

      {isLoadingAcreditation ? (
        <LoadingComponent />
      ) : (acreditation ?? []).length === 0 ? (
        <Box textAlign={'center'} my={3}>
          {t('no_acreditation_active_to_meeting')}
        </Box>
      ) : (
        <MeetingTable meetings={meetings} loading={isLoadingMeetings} refresh={refresh} />
      )}
      <Box className="agenda-title">
        <h2>{t('activities/programation')}</h2>
        <CalendarIcon />
      </Box>

      <ScheduleTable
        showsOfSchedule={
          searchParams.get('type_event') === 'quick_meet' ? [] : paginatedFavouriteShows
        }
        loading={isLoadingFavourites || loadingFavourites}
        type="agenda"
      />

      {!isLoadingFavourites && showingItems > 0 && <Pagination itemsTotal={showingItems} />}
    </Box>
  )
}

export default AgendaListPage
